import React, {useEffect, useMemo, useState} from 'react';

import {DataType} from 'src/components/data-types/data-type';
import {PossibleValues} from 'src/components/possible-values/possible-values';
import {Separator} from 'src/components/separator';
import {useAttributeUsage} from 'src/hooks/useAttributeUsage';
import {AttributeReviewFeedback, ChangeRequestMetadataReviewFeedback} from 'src/modules/ai';
import {AttributeColumnWidth, COMPARE_CHANGES_CAPTION} from 'src/modules/event-review/constants';
import {EventReviewActionBar} from 'src/modules/event-review/event-review-status/event-review-action-bar';
import {HighlighterWrapper} from 'src/modules/event-review/highlighter/highlighter-wrapper';
import {useHighlightsAttributesInfo} from 'src/modules/event-review/hooks/useHighlightsAttributesInfo';
import {getAttributeIds} from 'src/modules/event-review/utils/getAttributeIds';
import {AttributeChangeRequest} from 'src/openapi/models/AttributeChangeRequest';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {EventDetailInfo} from 'src/openapi/models/EventDetailInfo';
import {possibleValuesToString} from 'src/utils/possibleValuesToString';

import {CheckBox} from '@pb/kit-component-checkbox';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {Typography} from '@pb/kit-component-typography';

type Props = {
    changeRequest: ChangeRequest,
    changeRequestId: number | null,
    eventCodeInfo: EventDetailInfo | null,
    onStartReview: () => void,
    onStopReview: () => void,
    onSendToApprove: () => void,
    onApprove: () => void,
    onReopen: (openUpdateForm: boolean) => void,
    onReject: () => void,
    onShowUserFeedbackForm: () => void,
};

const NO_ATTRIBUTE_USAGE = 'No Usage';
const POSSIBLE_VALUES_FIELD_NAME = 'possibleValues';

export const Attributes = (props: Props) => {
    const {
        changeRequest,
        changeRequestId,
        eventCodeInfo,
        onStartReview,
        onStopReview,
        onSendToApprove,
        onApprove,
        onReopen,
        onReject,
        onShowUserFeedbackForm,
    } = props;

    const [attributes, setAttributes] = useState<Array<AttributeChangeRequest>>([]);
    const attributesIds = useMemo(() => getAttributeIds(attributes), [attributes]);
    const attributeUsages = useAttributeUsage(attributesIds);
    const highlightsInfo = useHighlightsAttributesInfo(changeRequest, eventCodeInfo, attributeUsages);

    useEffect(() => {
        setAttributes(changeRequest.event.attributes || []);
    }, [changeRequest]);

    const usageDate = (attribute: AttributeChangeRequest) => {
        const attributeUsage =
            attributeUsages.find(usage => usage.id === Number(attribute.id));

        return attributeUsage?.lastUsageDate ?? NO_ATTRIBUTE_USAGE;
    };

    return (
        <Group direction="v" gap="m">
            <Item style={{width: '100%'}}>
                <Typography variant="heading-level-4" fontWeight="semi-bold">Attributes:</Typography>
                <Separator/>
            </Item>
            <Group direction="v" gap="xls">
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={AttributeColumnWidth.attribute}>Attribute</Cell>
                        <Cell width={AttributeColumnWidth.description}>
                            Attribute Description
                        </Cell>
                        <Cell width={AttributeColumnWidth.dataType}>Data Type</Cell>
                        <Cell width={AttributeColumnWidth.possibleValues}>
                            <Group>
                                <Item>Possible Values</Item>
                                <ChangeRequestMetadataReviewFeedback
                                    changeRequestId={changeRequestId}
                                    fieldName={POSSIBLE_VALUES_FIELD_NAME}
                                />
                            </Group>
                        </Cell>
                        {changeRequest.event?.id ? (
                            <Cell width={AttributeColumnWidth.lastUsageDate}>Last Usage Date</Cell>
                        ) : null}
                        <Cell width={AttributeColumnWidth.required}>Required</Cell>
                        <Cell width={AttributeColumnWidth.deprecated}>Deprecated</Cell>
                    </Head>
                    <Body>
                        {attributes.map(attribute => (
                            <Row key={attribute.name} highlighted={!attribute.id && !!highlightsInfo}>
                                <Cell>
                                    <Group gap="s" justify="space-between" alignItems="center">
                                        <EllipsisTextHint>
                                            {attribute.name}
                                        </EllipsisTextHint>
                                        <AttributeReviewFeedback
                                            changeRequestId={changeRequestId}
                                            attributeName={attribute.name}
                                        />
                                    </Group>
                                </Cell>
                                <HighlighterWrapper
                                    caption={`${COMPARE_CHANGES_CAPTION} "${attribute.name}"`}
                                    actualValue={highlightsInfo?.[attribute.name]?.description.actualValue || ''}
                                    changeRequestValue={attribute.description || ''}
                                    tableCell={true}
                                    highlight={
                                        !!attribute.id &&
                                        !!highlightsInfo?.[attribute.name].description.highlight
                                    }>
                                    <span>{attribute.description}</span>
                                </HighlighterWrapper>
                                <Cell>
                                    <DataType
                                        dataType={attribute.fieldType}
                                        itemsType={attribute.itemsType}
                                    />
                                </Cell>
                                <HighlighterWrapper
                                    caption={`${COMPARE_CHANGES_CAPTION} "${attribute.name}"`}
                                    actualValue={highlightsInfo?.[attribute.name]?.possibleValues.actualValue || ''}
                                    changeRequestValue={possibleValuesToString(attribute.possibleValues)}
                                    tableCell={true}
                                    highlight={
                                        !!attribute.id &&
                                        !!highlightsInfo?.[attribute.name]
                                            .possibleValues.highlight
                                    }>
                                    <PossibleValues possibleValues={attribute.possibleValues}/>
                                </HighlighterWrapper>
                                {changeRequest.event?.id ? (
                                    <Cell verticalAlign="middle">
                                        <Group justify="center">{usageDate(attribute)}</Group>
                                    </Cell>
                                ) : null}
                                <HighlighterWrapper
                                    tableCell={true}
                                    clickable={false}
                                    highlight={
                                        !!attribute.id &&
                                        !!highlightsInfo?.[attribute.name].required.highlight
                                    }>
                                    <Group justify="center">
                                        <CheckBox disabled={true} checked={attribute.required}/>
                                    </Group>
                                </HighlighterWrapper>
                                <HighlighterWrapper
                                    tableCell={true}
                                    clickable={false}
                                    highlight={
                                        !!attribute.id &&
                                        !!highlightsInfo?.[attribute.name].deprecated.highlight
                                    }>
                                    <Group justify="center">
                                        <CheckBox disabled={true} checked={attribute.deprecated}/>
                                    </Group>
                                </HighlighterWrapper>
                            </Row>
                        ))}
                    </Body>
                </Table>
                <EventReviewActionBar
                    changeRequestId={changeRequestId}
                    status={changeRequest.status}
                    onStartReview={onStartReview}
                    onStopReview={onStopReview}
                    onSendToApprove={onSendToApprove}
                    onApprove={onApprove}
                    onReopen={onReopen}
                    onReject={onReject}
                    onShowUserFeedbackForm={onShowUserFeedbackForm}
                />
            </Group>
        </Group>
    );
};
